import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { VippsComponent } from '@shared/components/vipps/vipps.component';
import { MapDataService } from '@features/map/services/map-data.service';
import { map, of, take, tap } from 'rxjs';
import { DvButtonComponent, DvDividerComponent } from '@devinco/ui/dds';
import { RouteSegmentType, RouteSheetChipNames } from '@features/map/constants/constants';
import { RouteStatusComponent } from '@features/map/components/route-status/route-status.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { WeatherComponent } from '@shared/components/weather/weather.component';
import { PaymentRefPollingService } from '@shared/services/payment-ref-pooling.service';
import { DvSnackbarService } from '@devinco/ui/dds/snackbar';
import { SnackBarConfig } from '@shared/constants/snackbar-config';
import { RouteNetworkFavoritesService } from '@features/map/services/route-network-favorite.service';
import { SanitizationService } from '@shared/services/sanitization.service';
import { RouteHighlightService } from '@features/map/services/route-highlight.service';
import { RouteSectionFilter } from '@features/map/interfaces/map';

@Component({
  selector: 'app-route-network-detail',
  imports: [
    VippsComponent,
    RouteStatusComponent,
    DvButtonComponent,
    DvDividerComponent,
    TranslatePipe,
    WeatherComponent,
  ],
  templateUrl: './route-network-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './route-network-detail.component.scss',
})
export class RouteNetworkDetailComponent {
  private route = inject(ActivatedRoute);
  private dataService = inject(MapDataService);
  private router = inject(Router);
  private paymentRefPoolingService = inject(PaymentRefPollingService);
  private snackbar = inject(DvSnackbarService);
  private translate = inject(TranslateService);
  private favoritesService = inject(RouteNetworkFavoritesService);
  private sanitizationService = inject(SanitizationService);
  routeHighlightService = inject(RouteHighlightService);

  id = toSignal(this.route.paramMap.pipe(map((p) => p.get('id')!)));

  vippsPaymentStatus = computed(() => {
    const paymentRoutNetworkId = this.paymentRefPoolingService.routeNetworkId();
    const id = this.id();
    if (id !== paymentRoutNetworkId) return undefined;

    const paymentStatus = this.paymentRefPoolingService.paymentStatus();
    return paymentStatus;
  });

  routeNetwork = rxResource({
    params: () => {
      const id = this.id();
      return id ?? undefined;
    },

    stream: ({ params }) => {
      if (!params) return of(undefined);

      return this.dataService.getRouteNetworkById(params).pipe(
        map((routeNetwork) => ({
          ...routeNetwork,
          Description: routeNetwork.Description
            ? this.sanitizationService.sanitize(routeNetwork.Description)
            : undefined,
        })),
      );
    },
  });

  trailChips = computed(() => {
    const routeNetwork = this.routeNetwork.value();
    if (!routeNetwork) return [];

    const chips: { key: string; filter: RouteSectionFilter }[] = [];
    // classic/skating may come as segment types or features
    for (const name of RouteSheetChipNames) {
      const isNamed = (item: { Name: string }) => item.Name.toLowerCase() === name.toLowerCase();
      const feature = routeNetwork.RouteSegmentFeatures?.find(isNamed);
      const type = routeNetwork.RouteSegmentTypes?.find(isNamed);
      if (feature) {
        chips.push({
          key: feature.TranslationKey,
          filter: { featureId: feature.RouteSegmentFeatureID },
        });
      } else if (type) {
        chips.push({
          key: type.TranslationKey,
          filter: { segmentTypeId: type.RouteSegmentTypeID },
        });
      }
    }

    if (
      routeNetwork.RouteSegmentTypes?.some(
        (type) => type.RouteSegmentTypeID === RouteSegmentType.Walking,
      )
    ) {
      chips.push({
        key: 'Map.Legend.WalkingTrail',
        filter: { segmentTypeId: RouteSegmentType.Walking },
      });
    }
    return chips;
  });

  isFavorite = computed(() => {
    const routeNetwork = this.routeNetwork.value();
    return routeNetwork ? this.favoritesService.isFavorite(routeNetwork.RouteNetworkID!) : false;
  });

  toggleFavorite(): void {
    const routeNetwork = this.routeNetwork.value();
    if (!routeNetwork) return;

    this.favoritesService.toggle(routeNetwork);
  }

  donate(amount: number): void {
    const routeNetworkId = this.id();
    if (!routeNetworkId) return;

    this.dataService
      .donate(Number(routeNetworkId), amount, this.router.url)
      .pipe(
        take(1),
        tap((resp) => {
          window.location.href = resp.redirectUrl!;
          if (resp.reference) {
            this.paymentRefPoolingService.start(resp.reference, routeNetworkId);
          }
        }),
      )
      .subscribe();
  }

  cancelDonation(): void {
    const refId = this.paymentRefPoolingService.refId();
    if (!this.id() || !refId) return;

    this.dataService
      .cancelDonation(refId)
      .pipe(
        take(1),
        tap(() => {
          this.paymentRefPoolingService.stop();
          this.snackbar.open(
            this.translate.instant('Vipps.DonationCanceled'),
            SnackBarConfig.Success,
          );
        }),
      )
      .subscribe();
  }
}
